// --- CTA observer ---
const ctas = document.querySelectorAll(".cta");

if (ctas.length) {
    const ctaObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("in-view");
                } else {
                    entry.target.classList.remove("in-view");
                }
            });
        },
        { threshold: 0.75 }
    );

    ctas.forEach((cta) => ctaObserver.observe(cta));
}

// --- Stats observer ---
const stats = document.querySelectorAll(".stats");

if (stats.length) {
    const animateCount = (el) => {
        const finalText = el.textContent.trim();
        const rawTarget = el.dataset.target ?? finalText;
        const numericTarget = rawTarget.replace(/[^0-9]/g, "");
        const target = parseInt(numericTarget, 10);

        if (!Number.isFinite(target) || target <= 0) {
            el.textContent = finalText;
            return;
        }

        let current = 0;
        const duration = Number(el.dataset.duration ?? 1500);
        const stepTime = Math.max(Math.floor(duration / target), 20);
        const increment = Math.max(1, Math.round(target / (duration / stepTime)));

        el.textContent = "0";

        const timer = setInterval(() => {
            current += increment;

            if (current >= target) {
                el.textContent = finalText || String(target);
                clearInterval(timer);
            } else {
                el.textContent = String(current);
            }
        }, stepTime);
    };

    const statsObserver = new IntersectionObserver(
        (entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const numbers = entry.target.querySelectorAll(".stats-number");
                    numbers.forEach((num) => animateCount(num));
                    obs.unobserve(entry.target); // run once
                }
            });
        },
        { threshold: 0.3 }
    );

    stats.forEach((card) => statsObserver.observe(card));
}

// --- Sticky nav observer ---
const nav = document.querySelector("[data-product-nav]");

if (nav) {
    const items = Array.from(nav.querySelectorAll('a[href^="#"]'))
        .map((link) => {
            if (!link.hash) return null;
            const target = document.querySelector(link.hash);
            return target ? { link, target } : null;
        })
        .filter(Boolean);

    if (items.length) {
        // Placeholder element to preserve layout
        const placeholder = document.createElement("div");
        placeholder.setAttribute("aria-hidden", "true");
        placeholder.style.height = "0px";
        nav.parentElement.insertBefore(placeholder, nav);

        let lastScrollY = window.scrollY;

        const stickyObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const currentScrollY = window.scrollY;
                    const scrollingDown = currentScrollY > lastScrollY;
                    lastScrollY = currentScrollY;

                    if (!entry.isIntersecting && scrollingDown) {
                        // Scrolling down past nav → stick
                        placeholder.style.height = `${nav.offsetHeight}px`;
                        nav.classList.add("is-stuck");
                    } else if (entry.isIntersecting && !scrollingDown) {
                        // Scrolling up above nav → unstick
                        placeholder.style.height = "0px";
                        nav.classList.remove("is-stuck");
                    }
                });
            },
            { threshold: [1] } // fully out of view
        );

        stickyObserver.observe(placeholder);

        // Section active link observer
        const sectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const item = items.find((i) => i.target === entry.target);
                    if (item) {
                        item.link.classList.toggle("is-active", entry.isIntersecting);
                    }
                });
            },
            {
                rootMargin: "-50% 0px -50% 0px", // middle of viewport
                threshold: 0,
            }
        );

        items.forEach((item) => sectionObserver.observe(item.target));
    }
}

// --- Cards heading scroll animation ---
const cardsScrollSections = document.querySelectorAll(".section-cards-scroll");

if (cardsScrollSections.length) {
    const sections = Array.from(cardsScrollSections)
        .map((section) => {
            const container = section.querySelector(".container");
            if (!container) return null;
            const heading = container.querySelector("h2");
            if (!heading) return null;
            heading.style.willChange = "transform";
            return { section, container, heading };
        })
        .filter(Boolean);

    if (sections.length) {
        const remOffset = (() => {
            const base = parseFloat(getComputedStyle(document.documentElement).fontSize);
            return Number.isFinite(base) ? base * 8 : 128;
        })();
        const mediaQuery = window.matchMedia("(min-width: 1280px)");

        const updateSection = ({ container, heading }) => {
            const rect = container.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            if (rect.bottom <= 0 || rect.top >= viewportHeight) {
                heading.style.transform = "";
                return;
            }

            const maxOffset = Math.max(0, rect.height - heading.offsetHeight);
            if (maxOffset === 0) {
                heading.style.transform = "";
                return;
            }

            const translateY = Math.min(Math.max(remOffset - rect.top, 0), maxOffset);
            heading.style.transform = `translateY(${translateY}px)`;
        };

        const updateAll = () => {
            if (!mediaQuery.matches) {
                sections.forEach(({ heading }) => {
                    heading.style.transform = "";
                });
                return;
            }
            sections.forEach(updateSection);
        };

        let ticking = false;
        const requestTick = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                updateAll();
                ticking = false;
            });
        };

        window.addEventListener("scroll", requestTick, { passive: true });
        window.addEventListener("resize", requestTick);
        mediaQuery.addEventListener("change", requestTick);
        updateAll();
    }
}
